// Shared API + auth helpers for the Marketing & Media Command Centre.
// Uses fetch with the JWT token from localStorage.

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('auth_token') || localStorage.getItem('token') || '';

const isDemoMode = () => localStorage.getItem('mcc-demo-mode') === 'on';

const _headers = (extra = {}) => ({
  Authorization: `Bearer ${getToken()}`,
  ...(isDemoMode() ? { 'X-Demo-Mode': 'on' } : {}),
  ...extra,
});

const _handle = async (resp) => {
  if (!resp.ok) {
    const text = await resp.text();
    let msg = text;
    try { msg = JSON.parse(text).detail || text; } catch { /* ignore */ }
    throw new Error(msg || `${resp.status} ${resp.statusText}`);
  }
  const ct = resp.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) return resp.json();
  return resp;
};

export const mccGet = (path, params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return fetch(`${API}/api${path}${qs ? `?${qs}` : ''}`, { headers: _headers() }).then(_handle);
};

export const mccPost = (path, body) =>
  fetch(`${API}/api${path}`, {
    method: 'POST',
    headers: _headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  }).then(_handle);

export const mccPatch = (path, body) =>
  fetch(`${API}/api${path}`, {
    method: 'PATCH',
    headers: _headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  }).then(_handle);

export const mccDelete = (path) =>
  fetch(`${API}/api${path}`, {
    method: 'DELETE',
    headers: _headers(),
  }).then(_handle);

// Multipart upload with progress callback via XMLHttpRequest so we can show a bar.
export const mccUpload = (path, formData, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api${path}`);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    if (isDemoMode()) xhr.setRequestHeader('X-Demo-Mode', 'on');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { resolve({}); }
      } else {
        let msg = xhr.responseText;
        try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* ignore */ }
        reject(new Error(msg || `Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(formData);
  });

export const mccDownloadUrl = (fileId) => {
  const token = getToken();
  return `${API}/api/marketing/uploads/${fileId}/download?auth=${encodeURIComponent(token)}`;
};

export default {
  mccGet, mccPost, mccPatch, mccDelete, mccUpload, mccDownloadUrl,
};
